/**
 * LessonOrderPanel
 *
 * Slide-out panel opened from the top-right icon in ConversationView.
 * Shows lesson/order cards identical to the Lessons ("My Lessons") page for players
 * and the Orders (Lessons) page for coaches. Clicking a card opens the same detail
 * view used by those pages — no page navigation, everything renders within the panel.
 */

import { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile } from '../services/userProfile';
import { getOrder, computeOrderStatus } from '../services/orders';
import { useLessons } from '../contexts/LessonContext';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import type { UserProfile } from '../types/user';

// Shared card components
import PlayerLessonCard, { TrainingProgramAwaitingCard, CompletedBundleCard } from './PlayerLessonCard';
import CoachLessonCard from './CoachLessonCard';
import BackButton from './BackButton';

// Player detail views
import WaitingForLesson from './WaitingForLesson';
import LessonDeliveredDetail from './LessonDeliveredDetail';
import BundleProgress from './BundleProgress';
import BundleComplete from './BundleComplete';
import BundleLessonDetail from './BundleLessonDetail';
import PlayerProgressReport from './PlayerProgressReport';
import PlayerOrScreen from './PlayerOrScreen';

// Coach detail views
import CoachLessonDetail from './CoachLessonDetail';
import CoachBundleProgress from './CoachBundleProgress';
import CoachProgressReport from './CoachProgressReport';

interface LessonOrderPanelProps {
  /** All lessons belonging to this conversation */
  lessons: Lesson[];
  onClose: () => void;
  /** Coach single-lesson: skip the list and open this lesson's detail directly */
  autoOpenLesson?: Lesson;
  /**
   * When true AND autoOpenLesson resolves to a training-program detail,
   * forwards `autoStartLesson` to `CoachBundleProgress` so the editor
   * fires on mount. Powers the conversation banner's "Record lesson
   * breakdown" → editor short-cut.
   */
  autoStartLesson?: boolean;
}

// ── Detail view discriminated union ──────────────────────────────────────────

type PanelDetail =
  // Player
  | { type: 'waiting';          order: Order; lesson: Lesson }
  | { type: 'delivered';        order: Order; lesson: Lesson }
  | { type: 'bundle_complete';  order: Order }
  | { type: 'bundle_lesson';    order: Order; lesson: Lesson }
  | { type: 'bundle_progress';  order: Order }
  | { type: 'progress_report';  order: Order }
  | { type: 'or_screen';        order: Order }
  // Coach
  | { type: 'coach_lesson';          order: Order; lesson: Lesson }
  | { type: 'coach_bundle';          order: Order }
  | { type: 'coach_bundle_lesson';   order: Order; lesson: Lesson }
  | { type: 'coach_progress_report'; order: Order; finalLesson: Lesson };

// ── Main component ────────────────────────────────────────────────────────────

export default function LessonOrderPanel({ lessons, onClose, autoOpenLesson, autoStartLesson = false }: LessonOrderPanelProps) {
  const { currentMode } = useAuth();
  const { lessons: allLessons } = useLessons();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<Map<string, Order>>(new Map());
  const [playerProfiles, setPlayerProfiles] = useState<Map<string, UserProfile>>(new Map());
  // Internal nav stack — back pops to the user's actual previous panel.
  // Panel-internal state isn't tracked by browser history, so we track
  // our own. Mirrors /lessons and /orders.
  const [detailStack, setDetailStack] = useState<PanelDetail[]>([]);
  const detail: PanelDetail | null = detailStack[detailStack.length - 1] ?? null;
  const pushDetail = (view: PanelDetail) => setDetailStack(stack => [...stack, view]);
  const popDetail = () => setDetailStack(stack => stack.slice(0, -1));
  const autoOpenFiredRef = useRef(false);

  const isCoach = currentMode === 'coach';

  // ── Auto-open detail for single-lesson coach view ───────────────────────────

  useEffect(() => {
    if (!autoOpenLesson || autoOpenFiredRef.current) return;
    const order = autoOpenLesson.orderId ? orders.get(autoOpenLesson.orderId) : undefined;
    if (!order) return; // wait until order is loaded
    autoOpenFiredRef.current = true;
    if (isCoach) {
      if (order.type === 'training_program') {
        pushDetail({ type: 'coach_bundle', order }); // eslint-disable-line react-hooks/set-state-in-effect
      } else {
        pushDetail({ type: 'coach_lesson', order, lesson: autoOpenLesson });
      }
    } else {
      if (order.type === 'training_program') {
        pushDetail({ type: 'bundle_progress', order });
      } else if (autoOpenLesson.status === 'delivered') {
        pushDetail({ type: 'delivered', order, lesson: autoOpenLesson });
      } else {
        pushDetail({ type: 'waiting', order, lesson: autoOpenLesson });
      }
    }
  }, [autoOpenLesson, orders, isCoach]);

  // ── Fetch orders for lessons ────────────────────────────────────────────────

  useEffect(() => {
    const uniqueOrderIds = [...new Set(lessons.map(l => l.orderId).filter(Boolean))];
    uniqueOrderIds.forEach(orderId => {
      if (!orders.has(orderId)) {
        getOrder(orderId).then(order => {
          if (order) setOrders(prev => new Map(prev).set(orderId, order));
        }).catch(() => {});
      }
    });
  }, [lessons]);

  // ── Fetch player profiles (coach mode) ─────────────────────────────────────

  useEffect(() => {
    if (!isCoach) return;
    const uniquePlayerIds = [...new Set(lessons.map(l => l.playerId).filter(Boolean))];
    uniquePlayerIds.forEach(playerId => {
      if (!playerProfiles.has(playerId)) {
        getUserProfile(playerId).then(profile => {
          if (profile) setPlayerProfiles(prev => new Map(prev).set(playerId, profile));
        }).catch(() => {});
      }
    });
  }, [lessons, isCoach]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const getOrderForLesson = (lesson: Lesson): Order | undefined =>
    lesson.orderId ? orders.get(lesson.orderId) : undefined;

  const getOrderLessons = (order: Order): Lesson[] =>
    lessons.filter(l => l.orderId === order.id).sort((a, b) => (a.lessonNumber ?? 0) - (b.lessonNumber ?? 0));

  // ── Click handlers ───────────────────────────────────────────────────────────

  const handlePlayerCardClick = (lesson: Lesson, order: Order) => {
    if (lesson.status === 'reserved' || lesson.status === 'in_progress') {
      if (order.type === 'training_program') {
        pushDetail({ type: 'bundle_progress', order });
      } else {
        pushDetail({ type: 'waiting', order, lesson });
      }
    } else if (order.type === 'training_program') {
      pushDetail({ type: 'bundle_lesson', order, lesson });
    } else {
      pushDetail({ type: 'delivered', order, lesson });
    }
  };

  const handleCoachCardClick = (lesson: Lesson) => {
    onClose();
    navigate(`/orders?open=${lesson.id}`);
  };

  // ── Sorted lesson lists ──────────────────────────────────────────────────────

  const activeLessons = lessons
    .filter(l => l.status === 'reserved' || l.status === 'in_progress')
    .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));

  // Training program orders where the player needs to upload their next lesson video
  const trainingProgramsAwaitingUpload = !isCoach
    ? [...orders.values()]
        .filter(o => o.type === 'training_program')
        .filter(o => computeOrderStatus(o, getOrderLessons(o)) === 'active')
        .filter(o => {
          const ol = getOrderLessons(o);
          return !ol.some(l => l.status === 'in_progress' || l.status === 'reserved')
            && ol.some(l => l.status === 'delivered');
        })
        .map(o => {
          const ol = getOrderLessons(o);
          const delivered = ol.filter(l => l.status === 'delivered');
          const maxNum = Math.max(...delivered.map(l => l.lessonNumber ?? 0));
          const lastDelivered = delivered.find(l => l.lessonNumber === maxNum);
          return { order: o, nextLessonNum: maxNum + 1, lastDeliveredAt: lastDelivered?.deliveredAt };
        })
    : [];

  // Complete training program orders — shown as a single bundle card (player only)
  const completedBundles = !isCoach
    ? [...orders.values()]
        .filter(o => o.type === 'training_program')
        .filter(o => computeOrderStatus(o, getOrderLessons(o)) === 'complete')
        .map(o => ({ order: o, lessons: getOrderLessons(o) }))
        .sort((a, b) => (b.order.completedAt ?? b.order.updatedAt ?? '').localeCompare(a.order.completedAt ?? a.order.updatedAt ?? ''))
    : [];

  const completedBundleOrderIds = new Set(completedBundles.map(b => b.order.id));

  const deliveredLessons = lessons
    .filter(l => l.status === 'delivered')
    .filter(l => !completedBundleOrderIds.has(l.orderId ?? ''))
    .sort((a, b) => (b.deliveredAt ?? b.updatedAt ?? '').localeCompare(a.deliveredAt ?? a.updatedAt ?? ''));

  const allSorted = [...activeLessons, ...deliveredLessons];

  // ── Panel shell ──────────────────────────────────────────────────────────────

  const panelTitle = isCoach ? 'Order Details' : 'Lesson Details';

  return (
    <div className="fixed inset-y-0 md:left-[720px] left-0 right-0 bg-db-bg shadow-2xl border-l border-gray-200 z-50 flex flex-col">

      {/* Header */}
      <div className="h-[60px] px-5 flex items-center justify-between border-b border-db-border bg-white flex-shrink-0">
        {detail ? (
          <BackButton onClick={popDetail} size="sm" flush />
        ) : (
          <h2 className="text-[15px] font-bold text-db-text-1">{panelTitle}</h2>
        )}
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition"
          aria-label="Close panel"
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 5L5 15M5 5L15 15" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">

        {/* ── Detail view ─────────────────────────────────────────────────────── */}

        {detail && (() => {
          /* Re-resolve from the live arrays, same as Lessons.renderDetailBody.
             PanelDetail stores whole Order and Lesson objects captured when the
             row was opened, so the pane would otherwise render a snapshot while
             the list beside it re-renders from the subscription.

             NOTHING BELOW THIS LINE MAY READ detail.lesson OR detail.order
             DIRECTLY. The type cannot enforce it, so it is a rule. The snapshot
             stays only as an id carrier and as the fallback for the frame
             before the arrays arrive. */
          const lesson = 'lesson' in detail
            ? (allLessons.find(l => l.id === detail.lesson.id) ?? detail.lesson)
            : undefined;
          const order = 'order' in detail
            ? (orders.get(detail.order.id) ?? detail.order)
            : undefined;

          // PLAYER: waiting for lesson
          if (detail.type === 'waiting') {
            return (
              <WaitingForLesson
                order={order!}
                lesson={lesson!}
                allLessons={allLessons}
                onBack={popDetail}
              />
            );
          }

          // PLAYER: single/consult delivered
          if (detail.type === 'delivered') {
            // Suppress the "Continue with Coach X" cross-sell when the player
            // already has an active training-program order with this coach.
            // Conversation is per-coach so the orders Map is already scoped to
            // one coachId, but apply the predicate explicitly (coachId, type,
            // !refundRequested, active) to match the canonical shape used in
            // Lessons.tsx and to defend against any future cross-coach panel.
            const hasActiveProgramWithCoach = Array.from(orders.values()).some(o =>
              o.coachId === order!.coachId &&
              o.type === 'training_program' &&
              !o.refundRequested &&
              computeOrderStatus(o, getOrderLessons(o)) === 'active'
            );
            return (
              <LessonDeliveredDetail
                order={order!}
                lesson={lesson!}
                onBack={popDetail}
                onMessage={onClose}
                hasActiveProgramWithCoach={hasActiveProgramWithCoach}
              />
            );
          }

          // PLAYER: specific delivered lesson within bundle
          if (detail.type === 'bundle_lesson') {
            const orderLessons = getOrderLessons(order!);
            return (
              <BundleLessonDetail
                order={order!}
                lesson={lesson!}
                allOrderLessons={orderLessons}
                onBack={popDetail}
                onMessage={onClose}
              />
            );
          }

          // PLAYER: complete bundle overview
          if (detail.type === 'bundle_complete') {
            const orderLessons = getOrderLessons(order!);
            return (
              <BundleComplete
                order={order!}
                lessons={orderLessons}
                onLessonClick={l => pushDetail({ type: 'bundle_lesson', order: order!, lesson: l })}
                onBack={popDetail}
                onViewReport={() => pushDetail({ type: 'progress_report', order: order! })}
              />
            );
          }

          // PLAYER: progress report
          if (detail.type === 'progress_report') {
            return (
              <PlayerProgressReport
                order={order!}
                onBack={popDetail}
                onWhatsNext={() => pushDetail({ type: 'or_screen', order: order! })}
              />
            );
          }

          // PLAYER: or screen (what's next)
          if (detail.type === 'or_screen') {
            return (
              <PlayerOrScreen
                order={order!}
                onBack={popDetail}
              />
            );
          }

          // PLAYER: bundle progress overview
          if (detail.type === 'bundle_progress') {
            const orderLessons = getOrderLessons(order!);
            return (
              <BundleProgress
                order={order!}
                lessons={orderLessons}
                onLessonClick={l => pushDetail({ type: 'bundle_lesson', order: order!, lesson: l })}
                onActiveLessonClick={l => pushDetail({ type: 'waiting', order: order!, lesson: l })}
                onBack={popDetail}
              />
            );
          }

          // COACH: single/consult lesson
          if (detail.type === 'coach_lesson') {
            return (
              <CoachLessonDetail
                variant="panel"
                order={order!}
                lesson={lesson!}
                onBack={popDetail}
                onMessage={onClose}
              />
            );
          }

          // COACH: bundle progress
          if (detail.type === 'coach_bundle') {
            const orderLessons = getOrderLessons(order!);
            const totalLessons = order!.totalLessons ?? 4;
            const finalLesson = orderLessons.find(l => l.lessonNumber === totalLessons);
            return (
              <CoachBundleProgress
                order={order!}
                lessons={orderLessons}
                onBack={popDetail}
                onMessage={onClose}
                onWriteReport={() => {
                  if (finalLesson) {
                    pushDetail({ type: 'coach_progress_report', order: order!, finalLesson });
                  }
                }}
                onLessonClick={l => pushDetail({ type: 'coach_bundle_lesson', order: order!, lesson: l })}
                autoStartLesson={autoStartLesson}
              />
            );
          }

          // COACH: specific lesson within bundle (e.g. "Rewatch" delivered lesson)
          if (detail.type === 'coach_bundle_lesson') {
            return (
              <CoachLessonDetail
                variant="panel"
                order={order!}
                lesson={lesson!}
                onBack={popDetail}
                onMessage={onClose}
              />
            );
          }

          // COACH: progress report
          if (detail.type === 'coach_progress_report') {
            return (
              <CoachProgressReport
                order={order!}
                finalLesson={detail.finalLesson}
                onBack={popDetail}
                onSubmitted={popDetail}
              />
            );
          }

          return null;
        })()}

        {/* ── List view ────────────────────────────────────────────────────────── */}

        {!detail && (
          <div className="p-4 space-y-2.5">
            {allSorted.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="text-[13px] font-semibold text-db-text-2">No lessons yet</div>
              </div>
            ) : isCoach ? (
              /* Coach: flat list with CoachLessonCard */
              <>
                {activeLessons.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.07em] text-db-text-3 mb-2 px-1">
                      In Progress
                    </div>
                    <div className="space-y-2">
                      {activeLessons.map(lesson => {
                        const order = getOrderForLesson(lesson);
                        if (!order) return null;
                        return (
                          <CoachLessonCard
                            key={lesson.id}
                            lesson={lesson}
                            order={order}
                            playerProfile={playerProfiles.get(lesson.playerId)}
                            onClick={() => handleCoachCardClick(lesson)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                {deliveredLessons.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.07em] text-db-text-3 mb-2 px-1 mt-3">
                      Delivered
                    </div>
                    <div className="space-y-2">
                      {deliveredLessons.map(lesson => {
                        const order = getOrderForLesson(lesson);
                        if (!order) return null;
                        return (
                          <CoachLessonCard
                            key={lesson.id}
                            lesson={lesson}
                            order={order}
                            playerProfile={playerProfiles.get(lesson.playerId)}
                            onClick={() => handleCoachCardClick(lesson)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Player: active in bordered list, delivered as individual cards */
              <>
                {(activeLessons.length > 0 || trainingProgramsAwaitingUpload.length > 0) && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.07em] text-db-text-3 mb-2 px-1">
                      Active
                    </div>
                    <div className="border border-stroke-primary rounded-[10px] overflow-hidden bg-white divide-y divide-stroke-primary">
                      {activeLessons.map(lesson => {
                        const order = getOrderForLesson(lesson);
                        if (!order) return null;
                        return (
                          <Fragment key={lesson.id}>
                            <PlayerLessonCard
                              lesson={lesson}
                              order={order}
                              onClick={() => handlePlayerCardClick(lesson, order)}
                            />
                          </Fragment>
                        );
                      })}
                      {trainingProgramsAwaitingUpload.map(({ order, nextLessonNum, lastDeliveredAt }) => (
                        <Fragment key={order.id}>
                          <TrainingProgramAwaitingCard
                            order={order}
                            nextLessonNum={nextLessonNum}
                            lastDeliveredAt={lastDeliveredAt}
                            onClick={() => pushDetail({ type: 'bundle_progress', order })}
                          />
                        </Fragment>
                      ))}
                    </div>
                  </div>
                )}
                {(completedBundles.length > 0 || deliveredLessons.length > 0) && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.07em] text-db-text-3 mb-2 px-1 mt-3">
                      Delivered
                    </div>
                    <div className="space-y-2.5">
                      {completedBundles.map(({ order, lessons: bundleLessons }) => (
                        <CompletedBundleCard
                          key={order.id}
                          order={order}
                          lessons={bundleLessons}
                          onClick={() => pushDetail({ type: 'bundle_complete', order })}
                        />
                      ))}
                      {deliveredLessons.map(lesson => {
                        const order = getOrderForLesson(lesson);
                        if (!order) return null;
                        return (
                          <PlayerLessonCard
                            key={lesson.id}
                            lesson={lesson}
                            order={order}
                            onClick={() => handlePlayerCardClick(lesson, order)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
